import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import Button from '@/components_new/Button.vue'
import TextInput from '@/components_new/TextInput/TextInput.vue'
import { useConfig } from '@/composables/config'
import { useI18nAddon } from '@/i18n/i18n'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'

import * as api from './api'
import SearchResults, { SearchStatusResult } from './components/SearchResults.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Search',
  props: {
    visual: { default: 'rounded' },
    paddingSize: { default: 's' },
    inputClass: {}
  },
  emits: ["open", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const resultRef = ref<HTMLElement | null>(null)
const inputValue = ref('')
const isSearchOpened = ref(false)
const loggerStore = useLoggerStore()
const layoutStore = useLayoutStore()
const emit = __emit

const i18nAddon = useI18nAddon()
const router = useRouter()
const { GOOGLE_SEARCH_ID, GOOGLE_SEARCH_API_TOKEN } = useConfig()

function onOpen() {
    isSearchOpened.value = true
    emit('open')
}

function onClose() {
    isSearchOpened.value = false
    emit('close')
}
const queryEnabled = computed(() => inputValue.value.length > 1)

const onInput = useDebounceFn((value: string) => {
    inputValue.value = value.trim()
}, 500)

const status = computed((): SearchStatusResult => {
    if ((searchData.value && searchData.value.result.length === 0) || isError.value) {
        return 'empty'
    } else if (searchData.value) {
        return 'loaded'
    } else if (isLoading.value && inputValue.value) {
        return 'loading'
    }
    return 'initial'
})

const {
    isError,
    isLoading,
    isSuccess,
    data: searchData,
} = useQuery({
    queryKey: ['portal-search', inputValue, i18nAddon.currentLocale],
    enabled: queryEnabled,
    queryFn: () => {
        return api.doSearch({
            searchID: GOOGLE_SEARCH_ID,
            apiKey: GOOGLE_SEARCH_API_TOKEN,
            q: inputValue.value,
            locale: i18nAddon.currentLocale.value,
            lang: i18nAddon.getCurrentLanguage(),
            resolveRoute(route: string) {
                return router.resolve(route)
            },
        })
    },
    staleTime: Infinity,
    // we try to economy the search requests
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
})

watch(isSearchOpened, layoutStore.setFullscreen)

watch(isSuccess, (success) => {
    if (success && searchData.value) {
        loggerStore.logSearchEvent({
            search_result_count: searchData.value.totalItems,
            search_term: searchData.value.queryTerm,
        })
    }
})

function onClickOutside(event: MouseEvent) {
    if (!isSearchOpened.value) {
        return
    }

    const target = event.target as HTMLElement

    const isClickInsideResultSearch = resultRef.value?.contains(target)
    // if click was outside searchRef, but input still in focus — don't close
    const isInputFocused = resultRef.value?.contains(document.activeElement)
    // we don't reload header so we need to watch clicks on links
    const isLinkClick = target.closest('a')

    if ((!isInputFocused && !isClickInsideResultSearch) || isLinkClick) {
        onClose()
    }
}

function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isSearchOpened.value) {
        const activeElement = document.activeElement as HTMLElement
        onClose()
        activeElement.blur()
    }
}

onMounted(() => {
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeyDown)
    document.removeEventListener('click', onClickOutside)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "resultRef",
    ref: resultRef,
    class: _normalizeClass([isSearchOpened.value && _ctx.$style.search_opened])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(_ctx.$style.search)
    }, [
      _createVNode(TextInput, {
        id: "search",
        name: "search",
        color: "purple",
        visual: props.visual,
        type: "search",
        paddingSize: props.paddingSize,
        prefixIcon: "search",
        placeholder: _ctx.$tr('search.placeholder'),
        class: _normalizeClass([_ctx.inputClass, _ctx.$style.search_input]),
        autocomplete: "off",
        onFocus: onOpen,
        "onUpdate:modelValue": _unref(onInput)
      }, null, 8, ["visual", "paddingSize", "placeholder", "class", "onUpdate:modelValue"]),
      (isSearchOpened.value)
        ? (_openBlock(), _createBlock(Button, {
            key: 0,
            class: _normalizeClass(_ctx.$style.close_btn),
            interfaceTarget: "close-search",
            icon: "close",
            color: "carbon-400",
            onClick: onClose
          }, null, 8, ["class"]))
        : _createCommentVNode("", true)
    ], 2),
    (isSearchOpened.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", {
            class: _normalizeClass(_ctx.$style.overlay_blur),
            onClick: onClose
          }, null, 2),
          _createElementVNode("div", {
            class: _normalizeClass(_ctx.$style.resultsAnchor)
          }, [
            _createVNode(SearchResults, {
              searchResults: _unref(searchData),
              status: status.value
            }, null, 8, ["searchResults", "status"])
          ], 2)
        ], 64))
      : _createCommentVNode("", true)
  ], 2))
}
}

})